import React, { FC, ReactNode, useMemo } from 'react';
import {
  InfoLabel,
  Text,
  FollowersBadge,
  ItemTaxonomy,
  Tooltip,
  ButtonIcon,
} from '@cubeyou-team/hx-react-components';
import classnames from 'classnames';

import { fusionTypeLabelMap } from '@discover-frontend/constants';
import { QueryBadge } from '../query-badge';

interface QueryItemContentProps {
  label: string;
  source: string;
  selectedDataSource: string;
  itemType: string;
  followers_count: number;
  itemIcon: ReactNode;
  tooltipPosition: 'left' | 'right' | 'top' | 'bottom';
  taxonomy: string[];
  infoBox?: boolean;
  setMoreInfoOpen?: (show: boolean) => void;
  variant?: 'small' | 'medium';
}

export const QueryItemContent: FC<QueryItemContentProps> = ({
  label,
  source,
  selectedDataSource,
  itemType,
  followers_count,
  itemIcon,
  taxonomy,
  tooltipPosition = 'bottom',
  infoBox = false,
  setMoreInfoOpen,
  variant = 'medium',
}) => {
  const displayDataSourceBadge = useMemo(
    () => !fusionTypeLabelMap[source] && selectedDataSource,
    [source, selectedDataSource]
  );

  return (
    <div className="flex items-center gap-2">
      <div className="flex-shrink-0 w-12">{itemIcon}</div>
      <div className="flex-1 w-full flex items-center justify-between gap-1">
        <div className="flex flex-col gap-1 w-full">
          <Text
            title={label}
            size={variant === 'small' ? 'small' : 'xbody'}
            weight={variant === 'small' ? 'normal' : 'bold'}
          >
            {label}
          </Text>
          <div
            className={classnames(
              'flex items-center gap-1',
              variant === 'small' ? 'justify-between' : 'justify-start'
            )}
          >
            <div className="flex items-center gap-1">
              <InfoLabel label={itemType} />
              {source && <QueryBadge source={source} />}
              {displayDataSourceBadge && (
                <QueryBadge source={selectedDataSource} dataSourceBadge />
              )}
            </div>
            {followers_count && (
              <FollowersBadge
                followers={followers_count}
                icon="star_outline"
                size="xsmall"
                radius="large"
                showTooltip={false}
              />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <ItemTaxonomy
              taxonomy={taxonomy}
              tooltipPosition={tooltipPosition}
            />
          </div>
        </div>
        {infoBox && (
          <div className="flex-shrink-0 w-4">
            <Tooltip content="more info" position="left" isInfoLabel>
              <ButtonIcon
                disabled={false}
                className="hx-more-info-button"
                dataTestId="more-info-button"
                icon="informations"
                size="small"
                type="button"
                variant="dark"
                onClick={e => {
                  e.preventDefault();
                  setMoreInfoOpen(true);
                  e.stopPropagation();
                }}
              />
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};
